import type {
  ValidationQuickFillFocus,
  ValidationQuickFillSuggestedCellValue,
} from '../../../shared/types/validationNavigation.types';
import type { ValidationIssue } from '../types';

const END_TYPE_SLOT_SWAP_REPAIR_KIND = 'endtypeslotswap';

function normalizeFocusToken(value: string): string {
  return value.trim().toUpperCase().replace(/[\s_]+/g, '-');
}

function normalizeQuickFillRepairKind(value: string | undefined): string {
  return (value || '').trim().replace(/[^A-Za-z0-9]/g, '').toLowerCase();
}

function hasEndTypeSlotSwapRepairKind(issue: ValidationIssue): boolean {
  return (
    normalizeQuickFillRepairKind(issue.AdditionalData?.quickFillRepairKind) ===
    END_TYPE_SLOT_SWAP_REPAIR_KIND
  );
}

function normalizeQuickFillSuggestedCellLabel(value: unknown): 'End1' | 'End2' | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = normalizeFocusToken(value);
  if (normalized === 'END1' || normalized === 'END-TYPE-1') {
    return 'End1';
  }

  if (normalized === 'END2' || normalized === 'END-TYPE-2') {
    return 'End2';
  }

  return null;
}

export function parseEndTypeSlotSwapSuggestedCellValues(
  issue: ValidationIssue
): ValidationQuickFillSuggestedCellValue[] | null {
  const additionalData = issue.AdditionalData || {};
  if (
    normalizeQuickFillRepairKind(additionalData.quickFillRepairKind) !==
    END_TYPE_SLOT_SWAP_REPAIR_KIND
  ) {
    return null;
  }

  const rawSuggestedValues = additionalData.quickFillSuggestedCellValues;
  if (!rawSuggestedValues) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawSuggestedValues);
  } catch {
    return null;
  }

  if (!Array.isArray(parsed)) {
    return null;
  }

  const normalizedValues: ValidationQuickFillSuggestedCellValue[] = [];
  const valueByCell = new Map<string, string>();
  for (const item of parsed) {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const record = item as Record<string, unknown>;
    const elementId = typeof record.elementId === 'string' ? record.elementId.trim() : '';
    const label = normalizeQuickFillSuggestedCellLabel(record.label);
    const value = typeof record.value === 'string' ? record.value : null;
    if (!elementId || !label || value == null) {
      return null;
    }

    const cellKey = `${elementId}:${label}`;
    const existingValue = valueByCell.get(cellKey);
    if (existingValue != null && existingValue !== value) {
      return null;
    }

    valueByCell.set(cellKey, value);
    normalizedValues.push({
      elementId,
      label,
      value,
      ...(typeof record.currentValue === 'string' ? { currentValue: record.currentValue } : {}),
      ...(typeof record.reason === 'string' ? { reason: record.reason } : {}),
    });
  }

  return normalizedValues.length > 0 ? normalizedValues : null;
}

export function buildQuickFillSuggestedCellSignature(
  suggestedCellValues: ValidationQuickFillSuggestedCellValue[] | undefined
): string {
  return (suggestedCellValues ?? [])
    .map((cell) => `${cell.elementId}:${cell.label}:${cell.currentValue ?? ''}->${cell.value}`)
    .sort((left, right) => left.localeCompare(right))
    .join('|');
}

export interface EndTypeSlotSwapQuickFillHandoff {
  quickFillFocus: ValidationQuickFillFocus;
  issues: ValidationIssue[];
  elementIds: string[];
  suggestedCellValues: ValidationQuickFillSuggestedCellValue[];
}

export function buildEndTypeSlotSwapQuickFillHandoff(
  issues: ValidationIssue[]
): EndTypeSlotSwapQuickFillHandoff | null {
  const handoffIssues: ValidationIssue[] = [];
  const valueByCell = new Map<string, string>();
  const suggestedCellValueByCell = new Map<string, ValidationQuickFillSuggestedCellValue>();

  for (const issue of issues) {
    if (!hasEndTypeSlotSwapRepairKind(issue)) {
      continue;
    }

    const suggestedCellValues = parseEndTypeSlotSwapSuggestedCellValues(issue);
    if (!suggestedCellValues) {
      return null;
    }

    handoffIssues.push(issue);
    for (const cell of suggestedCellValues) {
      const cellKey = `${cell.elementId}:${cell.label}`;
      const existingValue = valueByCell.get(cellKey);
      if (existingValue != null && existingValue !== cell.value) {
        return null;
      }

      valueByCell.set(cellKey, cell.value);
      if (!suggestedCellValueByCell.has(cellKey)) {
        suggestedCellValueByCell.set(cellKey, cell);
      }
    }
  }

  if (handoffIssues.length === 0 || suggestedCellValueByCell.size === 0) {
    return null;
  }

  const suggestedCellValues = Array.from(suggestedCellValueByCell.values());
  const elementIds = Array.from(new Set(suggestedCellValues.map((cell) => cell.elementId)));
  const labels = (['End1', 'End2'] as const).filter((label) =>
    suggestedCellValues.some((cell) => cell.label === label)
  );

  return {
    quickFillFocus: {
      labels,
      primaryLabel: labels[0] ?? 'End1',
      rowCandidates: [],
      elementIds,
      familyTypeKeys: [],
      preferredRowMode: 'instance',
      autofillLabels: labels,
      autofillMode: 'overwrite',
      suggestedCellValues,
    },
    issues: handoffIssues,
    elementIds,
    suggestedCellValues,
  };
}
